import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "cx-surfacelog-analysis" }
const _hoisted_2 = { class: "cx-surfacelog-analysis__headers" }
const _hoisted_3 = { class: "analysis-title" }
const _hoisted_4 = {
  key: 0,
  class: "analysis-refmodel"
}
const _hoisted_5 = { class: "cx-surfacelog-analysis__chart-metrics" }
const _hoisted_6 = {
  key: 0,
  class: "cx-surfacelog-analysis__chart-content"
}
const _hoisted_7 = {
  key: 1,
  class: "cx-surfacelog-analysis__chart-content"
}

import { ref, Ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useVuex } from '@vueblocks/vue-use-vuex';
import { RefModel } from '@/domain/refmodels/RefModel';
import { Surfacelog } from '@/domain/surfacelog/Surfacelog';
import CxIcon from '@/components/CxIcon.vue';
import CxSurfacelogMetrics from './CxSurfacelogMetrics.vue';
import { gettext } from '@/translations/gettext.setup';
import { convert } from '@/utils/measurementUnitHelper';
import { MeasurementSystem } from '@/domain/MeasurementSystem';



export default /*#__PURE__*/_defineComponent({
  __name: 'CxSurfacelogCutFillAnalysis',
  setup(__props) {

const { $gettext } = gettext;
const store = useStore();
const mapStore = useVuex('map', store);
const detailsSurfacelogStore = useVuex('detailsSurfacelog', store);
const appStore = useVuex('app', store);

const lastRefmodelWithCalculatedDh: Ref<string | null> = ref(null);

const { selectedEntity } = mapStore.useGetters(['selectedEntity']) as { selectedEntity: Ref<Surfacelog> };
const {
  refModels,
  isDeltaHeightCalculated,
  selectedRefmodelId
} = detailsSurfacelogStore.useGetters([
  'refModels',
  'isDeltaHeightCalculated',
  'selectedRefmodelId'
]) as {
  refModels: Ref<RefModel[]>,
  selectedRefmodelId: Ref<(surfaceId: string) => string | null>
  isDeltaHeightCalculated: Ref<(refModelId: string, surfaceId: string) => boolean>,
};

const { measurementSystem } = appStore.useGetters(['measurementSystem']) as { measurementSystem: Ref<MeasurementSystem> };

const translations = computed(() => {
  return {
    cutFill: $gettext('Cut & Fill'),
    volume: $gettext('Volume'),
    area: $gettext('Area'),
    remaining: $gettext('Remaining'),
    noData: $gettext('no data')
  };
});

const convertToSquareMetres = (value: number) => {
  const result = convert(value).withPrecision(2).from(measurementSystem.value.baseLengthUnit.name).to('m');
  return result * result;
};
const convertToSquareUnits = (value: number) => {
  return convert(value).withPrecision(2).from('m2').to(measurementSystem.value.baseAreaUnit.name);
};
const convertToCubicUnits = (value: number) => {
  return convert(value).withPrecision(2).from('m3').to(measurementSystem.value.baseVolumeUnit.name);
};

const volumeKPI = computed(() => {
  const { cellSize, signalsKPI } = selectedEntity.value!;
  const convertedCellSizeArea = convertToSquareMetres(cellSize);
  const sum_neg = Math.round(convertToCubicUnits(Math.abs(signalsKPI.dH.sum_negative_value) * convertedCellSizeArea));
  const sum_pos = Math.round(convertToCubicUnits(Math.abs(signalsKPI.dH.sum_positive_value) * convertedCellSizeArea));
  const remaining = Math.round(convertToCubicUnits(sum_neg + sum_pos));
  const cut = Math.round(sum_pos / (sum_pos + Math.abs(sum_neg)) * 100);
  const fill = Math.round(Math.abs(sum_neg) / (sum_pos + Math.abs(sum_neg)) * 100);

  const chartData = [
    { label: 'cut', value: cut },
    { label: 'fill', value: fill }
  ];
  const kpiData = {
    cut: sum_pos,
    fill: sum_neg,
    remaining
  };
  return { chartData, kpiData };

});

const areaKPI = computed(() => {
  const { cellSize, signalsKPI } = selectedEntity.value;
  const convertedCellSizeArea = convertToSquareMetres(cellSize);
  const area_neg = Math.round(convertToSquareUnits(Math.abs(signalsKPI.dH.with_negative_value) * convertedCellSizeArea));
  const area_pos = Math.round(convertToSquareUnits(Math.abs(signalsKPI.dH.with_positive_value) * convertedCellSizeArea));
  const remaining = Math.round(convertToSquareUnits(area_neg + area_pos));
  const cut = Math.round(area_pos / remaining * 100);
  const fill = Math.round(area_neg / remaining * 100);

  const chartData = [
    { label: 'cut', value: cut },
    { label: 'fill', value: fill }
  ];
  const kpiData = {
    cut: area_pos,
    fill: area_neg,
    remaining
  };
  return { chartData, kpiData };
});


const cutFillRefmodel = computed(() => {
  if (selectedRefmodelId.value(selectedEntity.value!.uuid) || lastRefmodelWithCalculatedDh.value) {
    if (isDeltaHeightCalculated.value(selectedRefmodelId.value(selectedEntity.value!.uuid)!, selectedEntity.value!.uuid)) {
      return refModels.value.find(refmodel => refmodel.uuid === selectedRefmodelId.value(selectedEntity.value!.uuid));
    }
    return refModels.value.find(refmodel => refmodel.uuid === lastRefmodelWithCalculatedDh.value);
  }
  return null;
});

const showCalculationReferenceModel = computed(() => {
  if (cutFillRefmodel.value) {
    return isDeltaHeightCalculated.value(cutFillRefmodel.value!.uuid, selectedEntity.value!.uuid);
  }
  return false;
});

watch(() => selectedRefmodelId.value(selectedEntity.value?.uuid), (first, second) => {
  if (second && isDeltaHeightCalculated.value(second, selectedEntity.value?.uuid)) {
    lastRefmodelWithCalculatedDh.value = second;
  }

});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(translations.value.cutFill), 1),
      (showCalculationReferenceModel.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(CxIcon, {
              icon: "cx_ref_model",
              size: 12
            }),
            _createElementVNode("span", null, _toDisplayString(cutFillRefmodel.value?.name), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      (volumeKPI.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(CxSurfacelogMetrics, {
              pow: 3,
              metricsData: volumeKPI.value,
              title: translations.value.volume
            }, null, 8, ["metricsData", "title"])
          ]))
        : _createCommentVNode("", true),
      (areaKPI.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(CxSurfacelogMetrics, {
              pow: 2,
              metricsData: areaKPI.value,
              title: translations.value.area
            }, null, 8, ["metricsData", "title"])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})